import type {
  CRMUser,
  RfqProject,
  SupplierInvite,
  SupplierOrg,
  TimberProject,
  VendorCapability,
} from '../types';
import { rfqProjectReferencesSupplierOrg, rfqProjectSupplierOrgIds } from './rfqProjectSuppliers';
import { normalizeSupplierOrg, normalizeWorkType } from './vendorHelpers';

export type VendorCapabilityFilter = 'all' | 'timber' | 'cnc' | 'both';

export type VendorOrgLinkedProject = {
  rfqProjectId: string;
  dealId: string;
  dealTitle: string;
  workType: ReturnType<typeof normalizeWorkType>;
  status: RfqProject['status'];
  /** Project Hub id when resolvable (`/project-hub/:id?tab=suppliers`). */
  hubProjectId?: string;
};

export type VendorOrgLinkedPortalUser = {
  uid: string;
  displayName: string;
  email: string;
  isActive: boolean;
};

export type VendorOrgDirectoryRow = {
  org: SupplierOrg;
  orgId: string;
  capabilities: VendorCapability[];
  inviteCount: number;
  activeProjectCount: number;
  searchText: string;
};

export function buildVendorOrgDirectoryRows(
  supplierOrgs: SupplierOrg[],
  supplierInvites: SupplierInvite[],
  rfqProjects: RfqProject[],
): VendorOrgDirectoryRow[] {
  const invitesByOrg = new Map<string, SupplierInvite[]>();
  for (const inv of supplierInvites) {
    if (!inv.supplierOrgId) continue;
    const list = invitesByOrg.get(inv.supplierOrgId) || [];
    list.push(inv);
    invitesByOrg.set(inv.supplierOrgId, list);
  }

  const projectIdsByOrg = new Map<string, Set<string>>();
  for (const proj of rfqProjects) {
    if (!proj.id) continue;
    for (const orgId of rfqProjectSupplierOrgIds(proj)) {
      const set = projectIdsByOrg.get(orgId) || new Set();
      set.add(proj.id);
      projectIdsByOrg.set(orgId, set);
    }
    for (const invite of proj.timberSupplierInvites ?? []) {
      if (!invite.supplierOrgId || !proj.id) continue;
      const set = projectIdsByOrg.get(invite.supplierOrgId) || new Set();
      set.add(proj.id);
      projectIdsByOrg.set(invite.supplierOrgId, set);
    }
  }

  return supplierOrgs
    .filter((o) => o.id)
    .map((raw) => {
      const org = normalizeSupplierOrg(raw);
      const orgId = org.id!;
      const caps = org.capabilities || ['timber'];
      const invites = invitesByOrg.get(orgId) || [];
      const email = (org.defaultContactEmail || '').toLowerCase();
      const inviteEmails = invites.map((i) => i.email.toLowerCase()).join(' ');
      return {
        org,
        orgId,
        capabilities: caps,
        inviteCount: invites.length,
        activeProjectCount: projectIdsByOrg.get(orgId)?.size ?? 0,
        searchText: `${org.name} ${email} ${inviteEmails}`.toLowerCase(),
      };
    })
    .sort((a, b) => a.org.name.localeCompare(b.org.name, undefined, { sensitivity: 'base' }));
}

export function filterVendorDirectoryRows(
  rows: VendorOrgDirectoryRow[],
  query: string,
  capabilityFilter: VendorCapabilityFilter,
): VendorOrgDirectoryRow[] {
  const q = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (q && !row.searchText.includes(q)) return false;
    const hasTimber = row.capabilities.includes('timber');
    const hasCnc = row.capabilities.includes('cnc');
    switch (capabilityFilter) {
      case 'timber':
        return hasTimber;
      case 'cnc':
        return hasCnc;
      case 'both':
        return hasTimber && hasCnc;
      default:
        return true;
    }
  });
}

function resolveHubProjectIdForDeal(
  dealId: string,
  rfqTimberProjectId: string | undefined,
  timberProjects: TimberProject[] | undefined,
): string | undefined {
  const fromRfq = (rfqTimberProjectId || '').trim();
  if (fromRfq) return fromRfq;
  if (!timberProjects?.length || !dealId) return undefined;
  const contracted =
    timberProjects.find(
      (p) =>
        p.id
        && p.dealId === dealId
        && (p.phase === 'contracted' || p.phase === undefined || p.phase !== 'preconstruction'),
    ) || timberProjects.find((p) => p.id && p.dealId === dealId);
  return contracted?.id || undefined;
}

export function getLinkedProjectsForOrg(
  orgId: string,
  rfqProjects: RfqProject[],
  timberProjects?: TimberProject[],
): VendorOrgLinkedProject[] {
  const seen = new Set<string>();
  const out: VendorOrgLinkedProject[] = [];
  for (const proj of rfqProjects) {
    if (!proj.id || !rfqProjectReferencesSupplierOrg(proj, orgId)) continue;
    const key = `${proj.dealId}:${normalizeWorkType(proj.workType)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const hubProjectId = resolveHubProjectIdForDeal(proj.dealId, proj.timberProjectId, timberProjects);
    out.push({
      rfqProjectId: proj.id,
      dealId: proj.dealId,
      dealTitle: proj.dealTitle || proj.dealId,
      workType: normalizeWorkType(proj.workType),
      status: proj.status,
      ...(hubProjectId ? { hubProjectId } : {}),
    });
  }
  return out.sort((a, b) => a.dealTitle.localeCompare(b.dealTitle, undefined, { sensitivity: 'base' }));
}

/** Deep link into Project Hub Suppliers for a vendor-linked deal. */
export function vendorLinkedProjectHubPath(project: VendorOrgLinkedProject): string | null {
  const id = (project.hubProjectId || '').trim();
  return id ? `/project-hub/${encodeURIComponent(id)}?tab=suppliers` : null;
}

/** CRM login accounts linked to this vendor org (blocks directory deletion until removed). */
export function getLinkedPortalUsersForOrg(orgId: string, crmUsers: CRMUser[]): VendorOrgLinkedPortalUser[] {
  return crmUsers
    .filter((user) => user.supplierOrgId === orgId)
    .map((user) => ({
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      isActive: user.isActive,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }));
}

export function canDeleteVendorOrg(
  orgId: string,
  rfqProjects: RfqProject[],
  crmUsers: CRMUser[],
): boolean {
  return (
    getLinkedProjectsForOrg(orgId, rfqProjects).length === 0
    && getLinkedPortalUsersForOrg(orgId, crmUsers).length === 0
  );
}

export function getInvitesForOrg(orgId: string, supplierInvites: SupplierInvite[]): SupplierInvite[] {
  return supplierInvites
    .filter((i) => i.supplierOrgId === orgId)
    .slice()
    .sort((a, b) => {
      const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
      const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
      return tb - ta;
    });
}

export type SupplierOrgNotificationRecipient = {
  orgId: string;
  orgName: string;
  emails: string[];
  notificationFirstName: string;
};

/** First name from RFQ notifications — required before publish emails can send. */
export function orgRfqNotificationFirstName(org: SupplierOrg | undefined | null): string {
  return (org?.defaultContactFirstName || '').trim();
}

export function orgHasRfqNotificationFirstName(org: SupplierOrg | undefined | null): boolean {
  return !!orgRfqNotificationFirstName(org);
}

export function resolveOrgsMissingRfqNotificationFirstName(
  orgIds: string[],
  supplierOrgById: Map<string, SupplierOrg>,
): { orgId: string; orgName: string }[] {
  return orgIds
    .filter((orgId) => !orgHasRfqNotificationFirstName(supplierOrgById.get(orgId)))
    .map((orgId) => ({
      orgId,
      orgName: supplierOrgById.get(orgId)?.name || orgId,
    }));
}

/** Greeting first name for RFQ publish emails — from vendor RFQ notifications only. */
export function recipientFirstNameForOrg(
  orgId: string,
  supplierOrgById: Map<string, SupplierOrg>,
): string {
  const configured = orgRfqNotificationFirstName(supplierOrgById.get(orgId));
  if (!configured) return '';
  return configured.split(/\s+/)[0] || configured;
}

export function resolveSupplierOrgNotificationEmails(
  org: SupplierOrg | undefined | null,
  supplierInvites: SupplierInvite[],
): string[] {
  if (!org?.id) return [];
  const emails = new Set<string>();
  const defaultEmail = org.defaultContactEmail?.trim();
  if (defaultEmail && defaultEmail.includes('@')) {
    emails.add(defaultEmail.toLowerCase());
  }
  for (const inv of supplierInvites) {
    if (inv.supplierOrgId !== org.id) continue;
    if (inv.status === 'revoked') continue;
    const e = inv.email?.trim();
    if (e && e.includes('@')) emails.add(e.toLowerCase());
  }
  return Array.from(emails).sort((a, b) => a.localeCompare(b));
}

/**
 * Vendor Management "RFQ notifications" email only — used for quote approval.
 * Does not include portal users or supplier invites (those can be internal employees).
 */
export function resolveSupplierOrgRfqContactEmails(
  org: SupplierOrg | undefined | null,
): string[] {
  const defaultEmail = org?.defaultContactEmail?.trim();
  if (defaultEmail && defaultEmail.includes('@')) {
    return [defaultEmail.toLowerCase()];
  }
  return [];
}

export function resolvePublishRecipientsForOrgs(
  orgIds: string[],
  supplierOrgById: Map<string, SupplierOrg>,
  supplierInvites: SupplierInvite[],
): SupplierOrgNotificationRecipient[] {
  return orgIds.map((orgId) => {
    const org = supplierOrgById.get(orgId);
    return {
      orgId,
      orgName: org?.name || orgId,
      emails: resolveSupplierOrgNotificationEmails(org, supplierInvites),
      notificationFirstName: orgRfqNotificationFirstName(org),
    };
  });
}

/** Quote-approval recipients: RFQ notification contact only (not invites / portal users). */
export function resolveApprovalRecipientsForOrgs(
  orgIds: string[],
  supplierOrgById: Map<string, SupplierOrg>,
): SupplierOrgNotificationRecipient[] {
  return orgIds.map((orgId) => {
    const org = supplierOrgById.get(orgId);
    return {
      orgId,
      orgName: org?.name || orgId,
      emails: resolveSupplierOrgRfqContactEmails(org),
      notificationFirstName: orgRfqNotificationFirstName(org),
    };
  });
}
